import cs_analysis_fetch from './cs_analysis_fetch';

/**
 * Web-push enrolment for the comms notification loop (cs-analysis
 * design-client-comms.md §8). The service worker itself is registered from
 * src/index.js; this module handles permission + PushManager subscription +
 * registering the endpoint with cs-analysis.
 *
 * Call sites: the app-shell enrolment banner (components/PushEnrolBanner.jsx),
 * the Client Tickets queue header button ("Enable notifications"), and the
 * silent re-sync on load (index.js) + whenever the banner mounts.
 */

function url_base64_to_uint8(base64) {
    const padding = '='.repeat((4 - (base64.length % 4)) % 4);
    const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = window.atob(b64);
    return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

export function push_supported() {
    return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

// ── Pure environment helpers (tested in comms_push.test.js) ──────────────

/**
 * iPhone / iPad / iPod from UA + platform + touch points. iPadOS 13+ Safari
 * reports a Macintosh UA ("desktop-class browsing"); a Mac with more than
 * one touch point is an iPad — real Macs report 0.
 */
export function is_ios_device({ userAgent, platform, maxTouchPoints } = {}) {
    const ua = String(userAgent || '');
    const plat = String(platform || '');
    if (/iPhone|iPad|iPod/i.test(ua) || /iPhone|iPad|iPod/i.test(plat)) return true;
    return /Macintosh|MacIntel/i.test(ua + ' ' + plat) && Number(maxTouchPoints || 0) > 1;
}

/**
 * Launched from the Home Screen: iOS Safari exposes `navigator.standalone`,
 * everything else (Android / desktop installed PWA — and iOS too) matches the
 * `(display-mode: standalone)` media query.
 */
export function is_standalone_display({ navigatorStandalone, displayModeStandalone } = {}) {
    return navigatorStandalone === true || displayModeStandalone === true;
}

/**
 * What the enrolment banner should show. Order matters: on iOS, web push
 * only exists inside a Home-Screen app — a plain Safari tab has no
 * PushManager at all, so the install guide must win over "unsupported".
 *   'ios_install'  iPhone/iPad in a browser tab → Add to Home Screen guide
 *   'unsupported'  no service worker / PushManager / Notification
 *   'denied'       permission blocked in the browser → how to re-enable
 *   'granted'      permission ok → silently (re)subscribe; hide on success
 *   'prompt'       ask: one button → enable_push()
 */
export function push_enrol_state({ supported, permission, ios, standalone } = {}) {
    if (ios && !standalone) return 'ios_install';
    if (!supported) return 'unsupported';
    if (permission === 'denied') return 'denied';
    if (permission === 'granted') return 'granted';
    return 'prompt';
}

/** Read the live browser into the shape push_enrol_state() wants. */
export function read_push_env() {
    const nav = typeof navigator !== 'undefined' ? navigator : {};
    let display_standalone = false;
    try {
        display_standalone = !!(window.matchMedia && window.matchMedia('(display-mode: standalone)').matches);
    } catch (e) { /* matchMedia missing (old WebViews) */ }
    return {
        supported: push_supported(),
        permission: (typeof Notification !== 'undefined' && Notification.permission) || 'default',
        ios: is_ios_device({ userAgent: nav.userAgent, platform: nav.platform, maxTouchPoints: nav.maxTouchPoints }),
        standalone: is_standalone_display({ navigatorStandalone: nav.standalone, displayModeStandalone: display_standalone }),
    };
}

// ── Subscription ─────────────────────────────────────────────────────────

// Outcome of the most recent silent sync / enable in this page load:
// null (not run, or permission not granted) | 'subscribed' | 'failed'.
// The banner reads this so an already-enrolled browser never flashes it.
let last_sync = null;
let sync_in_flight = null;
export function last_push_sync() { return last_sync; }

/** Subscribe this browser (asks permission if needed). Returns 'subscribed' | 'denied' | 'unavailable'. */
export async function enable_push() {
    if (!push_supported()) return 'unavailable';
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return 'denied';

    const { key } = await cs_analysis_fetch({ path: '/api/v1/comms/push/vapid-public-key' });
    if (!key) return 'unavailable'; // backend not configured

    // .ready never resolves if registration failed — don't hang the button.
    const reg = await Promise.race([
        navigator.serviceWorker.ready,
        new Promise((_, rej) => setTimeout(() => rej(new Error('service worker not ready')), 8000)),
    ]);
    const wanted_key = url_base64_to_uint8(key);
    let sub = await reg.pushManager.getSubscription();
    if (sub) {
        // VAPID key rotation: an existing subscription under an old key can't
        // receive pushes signed with the new one — resubscribe (review fix).
        const cur = sub.options && sub.options.applicationServerKey
            ? new Uint8Array(sub.options.applicationServerKey) : null;
        const same = cur && cur.length === wanted_key.length && cur.every((b, i) => b === wanted_key[i]);
        if (!same) { await sub.unsubscribe().catch(() => {}); sub = null; }
    }
    if (!sub) {
        sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: wanted_key,
        });
    }
    const json = sub.toJSON();
    await cs_analysis_fetch({
        path: '/api/v1/comms/push/subscribe',
        method: 'POST',
        body: { endpoint: json.endpoint, keys: json.keys, userAgent: navigator.userAgent },
    });
    last_sync = 'subscribed';
    return 'subscribed';
}

/**
 * Silent re-sync: if permission already granted, make sure the backend has
 * this endpoint. Resolves true when subscribed, false otherwise; concurrent
 * callers (index.js load hook + the banner mount) share one attempt.
 */
export async function sync_push_if_granted() {
    try {
        if (!push_supported() || Notification.permission !== 'granted') return false;
        if (!sync_in_flight) {
            sync_in_flight = enable_push()
                .then((r) => { last_sync = r === 'subscribed' ? 'subscribed' : 'failed'; return r === 'subscribed'; })
                .catch(() => { last_sync = 'failed'; return false; })
                .then((ok) => { sync_in_flight = null; return ok; });
        }
        return await sync_in_flight;
    } catch (e) { return false; /* never block the app on push plumbing */ }
}
